// Llamadas a Odoo del flujo de registro de la fila virtual.
//
// Dos endpoints:
//  - /marketing-manager/import-data  (público): lead, dedup teléfono, crear dais.user
//  - /odoo-firebase-core/odoo-import (requiere access_token + auth_token Firebase):
//    verificación WhatsApp sobre dais.user (mismos métodos que consulta/landing).

const MARKETING_URL = 'https://odoo.core.daisies.mx/marketing-manager/import-data';
const CORE_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN || 'adc6b57347275d84196366a03ac4d288f8c79eae1c24e5e5992f28044789b679';
const ACCOUNT_ID = 1;
export const MX_COUNTRY_PHONE_ID = 156;

const postJson = async (url, params) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params }),
  });
  return res.json();
};

// ─── Lead / registro (marketing-manager) ───

// Dedup de teléfono/correo contra usuarios activos Y leads de fila. Si el
// backend aún no trae el método (deploy pendiente), regresamos fallback=true
// para que el caller use el check viejo de Firestore y no bloquee el registro.
export const checkSocialOutletPhone = async (phone10, email) => {
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'crm.lead',
      res_method: 'check_social_outlet_phone',
      res_id: 1,
      res_params: email ? { phone: phone10, email } : { phone: phone10 },
    });
    const result = data?.result;
    if (result?.success === true) {
      return {
        available: Boolean(result.available),
        reason: result.reason || null,
        hasConsultations: Boolean(result.has_consultations),
      };
    }
    return { available: true, fallback: true };
  } catch (err) {
    console.warn('[checkSocialOutletPhone] fallback:', err);
    return { available: true, fallback: true };
  }
};

// ─── Asociaciones (catálogo del dropdown + validación suave de código) ───

// Si el backend aún no trae los métodos (deploy pendiente) o falla la red,
// regresamos [] y el form degrada al texto libre de siempre.
export const fetchSocialAssociations = async () => {
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'dais.social.association',
      res_method: 'get_social_associations',
      res_id: 1,
      res_params: {},
    });
    return Array.isArray(data?.result) ? data.result : [];
  } catch (err) {
    console.warn('[fetchSocialAssociations] fallback a texto libre:', err);
    return [];
  }
};

// Validación en blur. error=true significa "no se pudo validar" (red/deploy):
// el form no marca inválido en ese caso, solo sigue sin verificar.
export const checkAssociationCode = async (associationId, code) => {
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'dais.social.association',
      res_method: 'check_association_code',
      res_id: 1,
      res_params: { association_id: Number(associationId), code: String(code).trim() },
    });
    if (data?.result && typeof data.result.valid === 'boolean') {
      return { valid: data.result.valid };
    }
    return { valid: false, error: true };
  } catch (err) {
    console.warn('[checkAssociationCode] no se pudo validar (no bloquea):', err);
    return { valid: false, error: true };
  }
};

export const createSocialOutletLead = async (resParams) => {
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'crm.lead',
      res_method: 'create_lead_from_social_outlet',
      res_id: 1,
      res_params: resParams,
    });
    return data?.result || { success: false };
  } catch (err) {
    console.error('[createSocialOutletLead] Error:', err);
    return { success: false, error: err.message };
  }
};

// Crea el dais.user a partir del lead (requiere el uid de Firebase Auth).
export const createDaisUserFromLead = async ({ leadId, email, firebaseUid }) => {
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'crm.lead',
      res_method: 'api_create_dais_user_from_lead',
      res_id: 1,
      res_params: { lead_id: leadId, email, firebase_uid: firebaseUid },
    });
    return data?.result || { success: false };
  } catch (err) {
    console.error('[createDaisUserFromLead] Error:', err);
    return { success: false, error: err.message };
  }
};

// WhatsApp "recibimos tu solicitud" tras verificar el OTP (best-effort).
// El número ya está confirmado en este punto; el backend manda la plantilla
// de Meta. Nunca bloquea el registro: si falla, solo se loggea.
export const sendSocialOutletWelcome = async (leadId) => {
  if (!leadId) return;
  try {
    await postJson(MARKETING_URL, {
      res_model: 'crm.lead',
      res_method: 'send_social_outlet_welcome',
      res_id: 1,
      res_params: { lead_id: leadId },
    });
  } catch (err) {
    console.warn('[sendSocialOutletWelcome] best-effort falló:', err);
  }
};

// ─── Verificación WhatsApp (odoo-firebase-core, port de consulta) ───

export const sendWhatsappCode = async ({ authToken, userId }) => {
  if (!authToken || !userId) {
    return { success: false, error: 'Falta authToken o userId.' };
  }
  try {
    const data = await postJson(CORE_URL, {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'send_whatsapp_verification_code',
      res_params: {},
    });
    const result = data?.result || {};
    // El backend devuelve el payload doblemente envuelto
    // (result.data.data.notification_id); toleramos también el plano.
    const notificationId =
      result?.data?.data?.notification_id ?? result?.data?.notification_id ?? null;
    if (result.success && notificationId != null) {
      return { success: true, notificationId: Number(notificationId) };
    }
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'No se pudo enviar el código.',
    };
  } catch (err) {
    console.warn('[sendWhatsappCode] error', err);
    return { success: false, error: err.message };
  }
};

export const validateWhatsappCode = async ({ authToken, userId, code, notificationId }) => {
  if (!authToken || !userId || !code || !notificationId) {
    return { success: false, error: 'Parámetros incompletos para validar el código.' };
  }
  try {
    const data = await postJson(CORE_URL, {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'validate_whatsapp_verification_code',
      res_params: {
        code: String(code).trim(),
        notification_id: Number(notificationId),
      },
    });
    const result = data?.result || {};
    if (result.success) {
      const inner = result.data?.data ?? result.data ?? {};
      return { success: true, phoneVerified: inner.phone_verified === true };
    }
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'Código inválido.',
    };
  } catch (err) {
    console.warn('[validateWhatsappCode] error', err);
    return { success: false, error: err.message };
  }
};

// ─── Confirmación de horario liberado (deep-link con token) ───

export const getConfirmContext = async (token) => {
  if (!token) return { ok: false, reason: 'invalid' };
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'dais.consultation.reservation',
      res_method: 'get_confirm_context',
      res_id: 1,
      res_params: { token },
    });
    return data?.result || { ok: false, reason: 'invalid' };
  } catch (err) {
    console.error('[getConfirmContext] Error:', err);
    return { ok: false, reason: 'error' };
  }
};

export const confirmOutletSlot = async (token, slotId) => {
  if (!token || !slotId) return { ok: false, reason: 'invalid' };
  try {
    const data = await postJson(MARKETING_URL, {
      res_model: 'dais.consultation.reservation',
      res_method: 'confirm_outlet_slot',
      res_id: 1,
      res_params: { token, slot_id: slotId },
    });
    return data?.result || { ok: false, reason: 'error' };
  } catch (err) {
    console.error('[confirmOutletSlot] Error:', err);
    return { ok: false, reason: 'error' };
  }
};

export const changeUserMobile = async ({ authToken, userId, mobile, countryPhoneId }) => {
  if (!authToken || !userId || !mobile) {
    return { success: false, error: 'Parámetros incompletos para cambiar teléfono.' };
  }
  try {
    const data = await postJson(CORE_URL, {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'change_user_mobile',
      res_params: {
        mobile: String(mobile).trim(),
        // Nunca null/0: rompe el envío del WhatsApp. Default a México (156).
        country_phone_id: countryPhoneId ? Number(countryPhoneId) : MX_COUNTRY_PHONE_ID,
      },
    });
    const result = data?.result || {};
    if (result.success) return { success: true };
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'No se pudo cambiar el teléfono.',
    };
  } catch (err) {
    console.warn('[changeUserMobile] error', err);
    return { success: false, error: err.message };
  }
};
